import { Stack, Typography } from '@mui/material';
import { AttachMoney, Chat, Check } from '@mui/icons-material';
import type { MouseEvent, ReactNode } from 'react';
import { format, isValid } from 'date-fns';
import { formatCurrency } from '~/shared/utils/formatCurrency';
import {
  AccessTimeIcon,
  AmountHighlight,
  CardActionButton,
  DescriptionTypography,
  ExpertChip,
  ExpertChipAvatar,
  ExpertChipName,
  ExpertChipStatus,
  ExpertsBlock,
  ExpertsList,
  FooterActions,
  FooterPartyBlock,
  FooterRow,
  FooterSection,
  HeadingTypography,
  Icon11,
  Icon16,
  IconCircle,
  MessageCountBadge,
  MobileActionContainer,
  PartyChip,
  PlaceholderAvatar,
  PartyInfoWrapper,
  RetainerStatusMetaLabel,
  StatsItem,
  StatsLabelTypography,
  StatsRow,
  HeaderLeftBlock,
  StyledCardLink,
  StyledCardWrapper,
  StyledContentWrapper,
  StyledHeader,
  StyledMatterCard,
  StyledRightControls,
} from '~/modules/matters/MatterCard.styles';
import type {
  Props,
  ExpertRetainerRollupStatus,
  ExpertRetainerStatus,
} from '~/modules/matters/MatterCard.types';
import type { User } from '~/shared/types/connect';
import { UserRole } from '~/shared/types';
import { useCurrentUser } from '~/shared/hooks/useCurrentUser';
import { getRetainerStatusDisplay } from '~/modules/matters/matterCardUtils';
import { useMatterCardActions } from '~/modules/matters/useMatterCardActions';

const AMOUNT_PATTERN = /(\d+ hrs|\$[\d,]+\.?\d*)/g;

function renderActiveRetainerWithAmountHighlight(text: string): ReactNode {
  const segments = text.split(AMOUNT_PATTERN);
  return segments.map((segment, index) =>
    /^\d+ hrs$/.test(segment) || /^\$[\d,]+\.?\d*$/.test(segment) ? (
      <AmountHighlight key={index}>{segment}</AmountHighlight>
    ) : (
      segment
    ),
  );
}

interface MatterCardHeaderProps {
  statusBadge: ReactNode;
  statusDropdown: ReactNode;
  matterHeading: string;
  descriptionText: string;
  updatedAt: string | null | undefined;
}

const MatterCardHeader = (props: MatterCardHeaderProps) => {
  const {
    statusBadge,
    statusDropdown,
    matterHeading,
    descriptionText,
    updatedAt,
  } = props;
  const date = updatedAt ? new Date(updatedAt) : null;
  const updatedDisplay =
    date && isValid(date) ? format(date, 'MMM d, yyyy') : '—';
  return (
    <>
      <StyledHeader>
        <HeaderLeftBlock>
          {statusDropdown}
          {statusBadge}
        </HeaderLeftBlock>
        <StyledRightControls>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <AccessTimeIcon color="action" aria-hidden />
            <Typography variant="caption" color="text.secondary">
              Updated {updatedDisplay}
            </Typography>
          </Stack>
        </StyledRightControls>
      </StyledHeader>
      <HeadingTypography variant="subtitle1">{matterHeading}</HeadingTypography>
      {descriptionText && (
        <DescriptionTypography variant="body2" color="text.secondary">
          {descriptionText}
        </DescriptionTypography>
      )}
    </>
  );
};

interface MatterCardStatsProps {
  showRetainerStatusInStats: boolean;
  retainerStatusMeta: {
    message: string;
    color: 'red' | 'warning' | 'success';
    icon: ReactNode;
  } | null;
  activeRetainerContent: ReactNode;
  totalBilled: number;
}

const MatterCardStats = (props: MatterCardStatsProps) => {
  const {
    showRetainerStatusInStats,
    retainerStatusMeta,
    activeRetainerContent,
    totalBilled,
  } = props;
  return (
    <StatsRow>
      {showRetainerStatusInStats && retainerStatusMeta && (
        <StatsItem>
          <RetainerStatusMetaLabel
            $color={retainerStatusMeta.color}
            variant="caption"
            fontWeight={600}
            component="span"
          >
            <Icon16 as="span">{retainerStatusMeta.icon}</Icon16>
            {retainerStatusMeta.message}
          </RetainerStatusMetaLabel>
        </StatsItem>
      )}
      <StatsItem $alignStart>
        <IconCircle $shrink>
          <Icon11 as="span" $block>
            <Check aria-hidden />
          </Icon11>
        </IconCircle>
        <StatsLabelTypography
          $lineHeightTight
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          component="span"
        >
          {activeRetainerContent}
        </StatsLabelTypography>
      </StatsItem>
      <StatsItem>
        <IconCircle>
          <Icon11 as="span">
            <AttachMoney aria-hidden />
          </Icon11>
        </IconCircle>
        <StatsLabelTypography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          component="span"
        >
          Total Billed:{' '}
          <AmountHighlight>{formatCurrency(totalBilled)}</AmountHighlight>
        </StatsLabelTypography>
      </StatsItem>
    </StatsRow>
  );
};

interface MessageActionProps {
  unreadMessageCount: number;
  onMessageClick: () => void;
  label?: string;
  mobile?: boolean;
  outline?: boolean;
  colorVariant?: 'default' | 'amount';
}

const MessageAction = (props: MessageActionProps) => {
  const {
    unreadMessageCount,
    onMessageClick,
    label = 'Message',
    mobile = false,
    outline = true,
    colorVariant,
  } = props;
  return (
    <MessageCountBadge
      badgeContent={unreadMessageCount}
      color="error"
      invisible={unreadMessageCount === 0}
    >
      <CardActionButton
        variant="outlined"
        size="small"
        aria-label={
          unreadMessageCount > 0
            ? `${label}, ${unreadMessageCount} unread`
            : label
        }
        startIcon={
          <Icon16 as="span">
            <Chat aria-hidden />
          </Icon16>
        }
        onClick={(event: MouseEvent) => {
          event.stopPropagation();
          onMessageClick();
        }}
        $mobile={mobile}
        $outline={outline}
        $colorVariant={colorVariant}
      >
        {label}
      </CardActionButton>
    </MessageCountBadge>
  );
};

interface MatterCardMobileActionsProps {
  mobileReopen: () => ReactNode;
  mobileAccept: () => ReactNode;
  mobileRequestMore: () => ReactNode;
  mobileViewRetainer: () => ReactNode;
  mobileRequestRetainer: () => ReactNode;
  onMessageClick: () => void;
  unreadMessageCount: number;
  messageLabel: string;
}

const MatterCardMobileActions = (props: MatterCardMobileActionsProps) => {
  const {
    mobileReopen,
    mobileAccept,
    mobileRequestMore,
    mobileViewRetainer,
    mobileRequestRetainer,
    onMessageClick,
    unreadMessageCount,
    messageLabel,
  } = props;
  return (
    <MobileActionContainer>
      {mobileReopen()}
      {mobileAccept()}
      {mobileRequestMore()}
      {mobileViewRetainer()}
      {mobileRequestRetainer()}
      <MessageAction
        unreadMessageCount={unreadMessageCount}
        onMessageClick={onMessageClick}
        label={messageLabel}
        mobile
        outline
      />
    </MobileActionContainer>
  );
};

interface MatterCardPartyBlockProps {
  showExpertsInFooter: boolean;
  visibleExperts: User[];
  remainingExpertCount: number;
  expertRetainerStatuses: ExpertRetainerStatus[] | undefined;
  otherParty: User | null;
  otherPartyDisplayName: string;
  otherPartyRetainerStatus: ExpertRetainerRollupStatus | undefined;
}

const MatterCardPartyBlock = (props: MatterCardPartyBlockProps) => {
  const {
    showExpertsInFooter,
    visibleExperts,
    remainingExpertCount,
    expertRetainerStatuses,
    otherParty,
    otherPartyDisplayName,
    otherPartyRetainerStatus,
  } = props;
  const { user: currentUser, isLoading: isCurrentUserLoading } =
    useCurrentUser();
  const viewerIsExpert = currentUser?.role === UserRole.EXPERT;
  if (showExpertsInFooter) {
    return (
      <ExpertsBlock>
        <ExpertsList>
          {visibleExperts.map((expert) => {
            const retainerStatus = expertRetainerStatuses?.find(
              (status) => status.expertId === expert.id,
            )?.status;
            const display = getRetainerStatusDisplay(retainerStatus ?? null);
            return (
              <ExpertChip
                key={expert.id}
                to={`/experts/${expert.id}`}
                onClick={(event: MouseEvent) => event.stopPropagation()}
              >
                <ExpertChipAvatar
                  avatarUrl={expert.avatarUrl}
                  initialsLabel={expert.name ?? ''}
                  alt={expert.name ?? ''}
                />
                <ExpertChipName variant="caption">
                  {expert.name ?? 'Expert'}
                </ExpertChipName>
                {display && (
                  <ExpertChipStatus
                    variant="caption"
                    component="span"
                    $color={display.color}
                  >
                    {display.label}
                  </ExpertChipStatus>
                )}
              </ExpertChip>
            );
          })}
          {remainingExpertCount > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              component="span"
            >
              +{remainingExpertCount} more
            </Typography>
          )}
        </ExpertsList>
      </ExpertsBlock>
    );
  }
  if (otherParty) {
    const otherPartyHref =
      otherParty.role === UserRole.EXPERT
        ? `/experts/${otherParty.id}`
        : `/attorneys/${otherParty.id}`;
    const otherPartyAriaLabel =
      otherParty.role === UserRole.EXPERT
        ? `View ${otherPartyDisplayName}'s expert profile`
        : `View ${otherPartyDisplayName}'s profile`;
    const otherPartyDisplay = getRetainerStatusDisplay(
      otherPartyRetainerStatus ?? null,
    );
    const suppressLink =
      otherParty.role !== UserRole.EXPERT &&
      (isCurrentUserLoading || viewerIsExpert);
    const chipContent = (
      <>
        <ExpertChipAvatar
          avatarUrl={otherParty.avatarUrl}
          initialsLabel={otherPartyDisplayName}
          alt={otherPartyDisplayName}
        />
        <ExpertChipName variant="caption" $noTruncate>
          {otherPartyDisplayName}
        </ExpertChipName>
        {otherPartyDisplay && (
          <ExpertChipStatus
            variant="caption"
            component="span"
            $color={otherPartyDisplay.color}
          >
            {otherPartyDisplay.label}
          </ExpertChipStatus>
        )}
      </>
    );
    return (
      <ExpertsBlock>
        <ExpertsList>
          {suppressLink ? (
            <PartyChip>{chipContent}</PartyChip>
          ) : (
            <ExpertChip
              to={otherPartyHref}
              onClick={(event: MouseEvent) => event.stopPropagation()}
              aria-label={otherPartyAriaLabel}
            >
              {chipContent}
            </ExpertChip>
          )}
        </ExpertsList>
      </ExpertsBlock>
    );
  }
  return (
    <>
      <PlaceholderAvatar alt="" aria-hidden>
        —
      </PlaceholderAvatar>
      <PartyInfoWrapper>
        <Typography variant="caption" color="text.secondary">
          Invite experts
        </Typography>
      </PartyInfoWrapper>
    </>
  );
};

interface MatterCardFooterProps {
  partyContent: ReactNode;
  actions: ReactNode;
}

const MatterCardFooter = (props: MatterCardFooterProps) => {
  const { partyContent, actions } = props;
  return (
    <FooterSection>
      <FooterRow>
        <FooterPartyBlock>{partyContent}</FooterPartyBlock>
        <FooterActions>{actions}</FooterActions>
      </FooterRow>
    </FooterSection>
  );
};

const MatterCard = (props: Props) => {
  const {
    matter,
    otherParty,
    totalBilled,
    hasPendingRetainer,
    hasFundedRetainer,
    activeRetainerAmount = '0 active retainers',
    expertRetainerStatuses,
    otherPartyRetainerStatus,
  } = props;

  const {
    descriptionText,
    hasDesktopActions,
    matterHeading,
    retainerStatusMeta,
    showExpertsInFooter,
    statusBadge,
    leftBorderVariant,
    visibleExperts,
    remainingExpertCount,
    otherPartyDisplayName,
    isClosed,
    mobileReopen,
    mobileAccept,
    mobileRequestMore,
    mobileViewRetainer,
    mobileRequestRetainer,
    getReopenButton,
    getAcceptRequestButton,
    getRequestRetainerButton,
    getRequestMoreButton,
    getViewRetainerButton,
    navigateToChat,
    unreadMessageCount,
    messageLabel,
    statusDropdown,
  } = useMatterCardActions(props);

  const showRetainerStatusInStats =
    retainerStatusMeta &&
    !(showExpertsInFooter && (expertRetainerStatuses?.length ?? 0) > 0) &&
    (otherPartyRetainerStatus === null ||
      otherPartyRetainerStatus === undefined);

  const showFooterOrMobileActions =
    (hasPendingRetainer && !hasFundedRetainer) ||
    hasFundedRetainer ||
    (otherParty !== null && otherParty !== undefined) ||
    showExpertsInFooter ||
    hasDesktopActions;

  return (
    <StyledMatterCard variant="outlined" $leftBorderVariant={leftBorderVariant}>
      <StyledCardWrapper>
        <StyledCardLink
          to={`/matters/${matter.id}`}
          aria-label={`View matter: ${matterHeading}`}
        />
        <StyledContentWrapper>
          <MatterCardHeader
            statusBadge={statusBadge}
            statusDropdown={statusDropdown}
            matterHeading={matterHeading}
            descriptionText={descriptionText}
            updatedAt={matter.updatedAt}
          />
          <MatterCardStats
            showRetainerStatusInStats={Boolean(showRetainerStatusInStats)}
            retainerStatusMeta={retainerStatusMeta}
            activeRetainerContent={renderActiveRetainerWithAmountHighlight(
              activeRetainerAmount,
            )}
            totalBilled={totalBilled}
          />
          {showFooterOrMobileActions && (
            <MatterCardMobileActions
              mobileReopen={mobileReopen}
              mobileAccept={mobileAccept}
              mobileRequestMore={mobileRequestMore}
              mobileViewRetainer={mobileViewRetainer}
              mobileRequestRetainer={mobileRequestRetainer}
              onMessageClick={() => navigateToChat(matter.id)}
              unreadMessageCount={unreadMessageCount}
              messageLabel={messageLabel}
            />
          )}
          {showFooterOrMobileActions && (
            <MatterCardFooter
              partyContent={
                <MatterCardPartyBlock
                  showExpertsInFooter={showExpertsInFooter}
                  visibleExperts={visibleExperts}
                  remainingExpertCount={remainingExpertCount}
                  expertRetainerStatuses={expertRetainerStatuses}
                  otherParty={otherParty}
                  otherPartyDisplayName={otherPartyDisplayName}
                  otherPartyRetainerStatus={otherPartyRetainerStatus}
                />
              }
              actions={
                <>
                  <MessageAction
                    unreadMessageCount={unreadMessageCount}
                    onMessageClick={() => navigateToChat(matter.id)}
                    label={messageLabel}
                    mobile={false}
                    outline
                    colorVariant="amount"
                  />
                  {isClosed ? (
                    getReopenButton(false)
                  ) : (
                    <>
                      {hasPendingRetainer && !hasFundedRetainer
                        ? getAcceptRequestButton(false)
                        : null}
                      {getRequestRetainerButton(false)}
                      {hasFundedRetainer &&
                        (getViewRetainerButton(false) ??
                          getRequestMoreButton(false))}
                    </>
                  )}
                </>
              }
            />
          )}
        </StyledContentWrapper>
      </StyledCardWrapper>
    </StyledMatterCard>
  );
};

export {
  MatterCard,
  MatterCardHeader,
  MatterCardStats,
  MatterCardMobileActions,
  MatterCardPartyBlock,
  MatterCardFooter,
};
